import { addQueryParamsToUrl, twClassMerge, useMessageChannelContext  } from '@healthhub/react-ui';
import { Skeleton, Typography } from 'antd';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import { ErrorScreenWrapper } from '@shared/components/error';
import { FAMILY_FRIENDS_SECTION_ANCHOR_KEY } from '@shared/constants/anchorKey';
import type { SwitchProfileTypeType } from '@shared/constants/constants';
import { RelatedPeopleStatus } from '@shared/constants/constants';
import { ROOT_PATH } from '@shared/constants/paths';
import { SERVICE_CATALOG_CODE } from '@shared/constants/serviceCatalog';
import { useLayoutViewContext } from '@shared/context';
import { useCurrentUserOrCurrentFederatedUser } from '@shared/hooks/useCurrentUserOrCurrentFederatedUser';
import useMemberIdentifier from '@shared/hooks/useMemberIdentifier';
import useRelatedPeople from '@shared/hooks/useRelatedPeople';
import { useAppDispatch } from '@shared/stores/rootTypes';
import type { ISelectedProfile } from '@shared/stores/user/user.types';
import { userAction } from '@shared/stores/user/userSlice';
import { MESSAGE_ACTION } from '@shared/utils/bridge';
import { checkEmptyRelatedPeople } from '@shared/utils/profile/checkEmptyRelatedPeople';


import Button from '../../Button/Button';
import ProfileListing from '../ProfileListing/ProfileListing';

interface Props {
  allowCurrentUserNavigation?: boolean;
  bypassEmptyCheckAndShowSelf?: boolean;
  customSwitchUserNavigatePath?: string;
  customSwitchUserReplaceHistory?: boolean;
  onCloseDropdown?: () => void;
  pageLevel?: number;
  showSelfInSwitchProfile?: boolean;
  switchProfileType: SwitchProfileTypeType;
}

const SwitchProfileDropdown = ({
  allowCurrentUserNavigation = false,
  bypassEmptyCheckAndShowSelf = false,
  customSwitchUserNavigatePath,
  customSwitchUserReplaceHistory = true,
  onCloseDropdown,
  pageLevel,
  showSelfInSwitchProfile,
  switchProfileType,
}: Props) => {
  const { isMobileView, isPortalView, isSharedServiceView } = useLayoutViewContext();
  const { sendMessageToNative } = useMessageChannelContext();

  const dispatch = useAppDispatch();
  const routerNavigate = useNavigate();
  const { t } = useTranslation();
  const { data, isFetching } = useRelatedPeople();
  const { userProfile } = useCurrentUserOrCurrentFederatedUser({
    isPatientMasterInfoRequired: true,
  });

  const [, setSearchParams] = useSearchParams();
  const { memberIdentifier } = useMemberIdentifier();

  const handleSelectedProfile = (id: string, sourceCode: string, name: string, nric: string) => {
    if (((!memberIdentifier && !id) || memberIdentifier === id) && !allowCurrentUserNavigation) {
      onCloseDropdown?.();
      return;
    }

    dispatch(
      userAction.setSelectedProfile({
        memberIdentifier: id,
        sourceCode,
        name,
        nric,
      } as ISelectedProfile)
    );

    if (customSwitchUserNavigatePath) {
      if (pageLevel && window.history.length > pageLevel + 1) {
        routerNavigate(-pageLevel);
      }

      setTimeout(() => {
        routerNavigate(
          addQueryParamsToUrl(customSwitchUserNavigatePath, {
            mid: id,
          }),
          {
            replace: customSwitchUserReplaceHistory,
          }
        );
      }, 0);
    } else {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set('mid', id);
          return p;
        },
        { replace: true }
      );
    }

    onCloseDropdown?.();
  };

  const onClickHereMobile = () => {
    sendMessageToNative(
      MESSAGE_ACTION.navigation({
        code: SERVICE_CATALOG_CODE.careCircle,
        attributes: { launchMode: 'REPLACE_INSTANCE' },
      })
    );
  };

  const onClickHerePortal = () => {
    routerNavigate({
      pathname: ROOT_PATH,
      hash: FAMILY_FRIENDS_SECTION_ANCHOR_KEY,
    });
  };

  const renderEmptyState = () => {
    if (isMobileView || isSharedServiceView)
      return (
        <section className="mb-5 flex flex-col">
          <Typography.Text className="mb-5 inline-block text-base font-semibold">
            {t('profileList.emptyMessage')}
          </Typography.Text>
          {!isSharedServiceView && (
            <Button
              className="cursor-pointer text-sm font-semibold text-theme-600"
              onClick={onClickHereMobile}
              variant="link"
            >
              {t('profileList.addChildrenFamilyAndFriends')}
            </Button>
          )}
        </section>
      );

    return (
      <section className="mb-5 flex flex-col gap-y-4">
        <Typography.Text className="inline-block text-base font-semibold">
          {t('profileList.emptyMessage')}
        </Typography.Text>
        <Button
          className="cursor-pointer text-sm font-semibold text-theme-600"
          onClick={onClickHerePortal}
          variant="link"
        >
          {t('profileList.addChildrenFamilyAndFriends')}
        </Button>
      </section>
    );
  };

  return (
    <div
      className={twClassMerge('bg-white px-4', {
        'pb-5': !isMobileView && !isSharedServiceView,
        'pb-7': isMobileView || isSharedServiceView,
      })}
    >
      {isFetching ? (
        <section className="flex flex-col gap-y-4 py-4">
          <Skeleton.Button active className="h-20 w-full" />
          <Skeleton.Button active className="h-20 w-full" />
          <Skeleton.Button active className="h-20 w-full" />
        </section>
      ) : (
        <>
          <ErrorScreenWrapper
            emptyStateProps={{
              isEmpty: checkEmptyRelatedPeople(data, switchProfileType, bypassEmptyCheckAndShowSelf),
              emptyStateScreenProps: {
                message: renderEmptyState(),
              },
            }}
          >
            <div className={twClassMerge('w-full bg-white py-2.5', isPortalView && 'max-h-[610px] overflow-y-auto')}>
              <ProfileListing
                careDependants={data?.FamilyAndFriend?.filter(
                  (profile) => profile.Status === RelatedPeopleStatus.APPROVED
                )}
                childrens={data?.Children?.filter((profile) => profile.Status === RelatedPeopleStatus.APPROVED)}
                handleSelectProfile={handleSelectedProfile}
                showSelfInSwitchProfile={showSelfInSwitchProfile}
                switchProfileType={switchProfileType}
                userProfile={userProfile}
              />
            </div>
          </ErrorScreenWrapper>
          <div className="flex justify-center pt-4">
            <Button
              block
              className={twClassMerge('bg-white', {
                'max-w-[343px]': !isMobileView && !isSharedServiceView,
              })}
              onClick={onCloseDropdown}
              variant="secondary"
            >
              Cancel
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default memo(SwitchProfileDropdown);
